import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import {
  ExportChannelsPicker,
  useExportChannelsStyles
} from '@cube3/ui/src/exports/ExportChannelsPicker';
import {
  ContentfulSpace,
  ContentfulConfig
} from '@cube3/common/model/resource-types';
import { ExportTarget } from '@cube3/common/model/schema/resources/export-target';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import { useModalActions } from '../../Modals/modalActions';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';

const emptyArray = [];

export const ContentfulAccountsModal = (props) => {
  const { modalContext } = props;
  const { exportTargetTypeId } = modalContext;

  const [selectedAccount, setSelectedAccount] =
    useState<ExportTarget<'contentful'>>(null);
  const [selectedSpace, setSelectedSpace] = useState<ContentfulSpace>(null);

  const { previousModal, openModal } = useModalActions();
  const [currentAccountId] = useCurrentAccount();

  const params = useMemo(() => {
    return {
      filter: {
        exportTargetTypeId: exportTargetTypeId // NOTE: not implemented
      }
    };
  }, [exportTargetTypeId]);

  const {
    resources: exportTargets,
    status: exportTargetStatus
  }: { resources?: ExportTarget<'contentful'>[]; status?: string } =
    useResourceList__ALPHA({
      resourceId: currentAccountId,
      resourceType: 'account',
      edgeType: 'export-target',
      edgeLabel: 'export-targets',
      params,
      strategy: 'fetch-on-mount'
    });

  /** the spaces of the selected account */
  const spaces =
    (selectedAccount && selectedAccount.attributes.integration_config.spaces) ||
    emptyArray;

  const handleNext = useCallback(() => {
    openModal(
      'social_export_options',
      {
        space: selectedSpace,
        exportTargetId: selectedAccount?.id,
        exportTargetTypeId: exportTargetTypeId,
        platform: 'Contentful'
      },
      false
    );
  }, [openModal, selectedAccount, selectedSpace, exportTargetTypeId]);

  const handleManageIntegration = useCallback(() => {
    // open modal version of Account’s Integrations page
    openModal('connected_accounts_modal', null, false);
  }, [openModal]);

  const handleSelectAccount = useCallback((account) => {
    setSelectedAccount(account);
    setSelectedSpace(
      account.channels && account.channels.length === 1
        ? account.channels[0]
        : undefined
    );
  }, []);

  useEffect(() => {
    if (!selectedAccount && exportTargets?.length > 0) {
      setSelectedAccount(exportTargets[0]);
    }
    if (
      selectedAccount &&
      !exportTargets.find((ac) => ac.id === selectedAccount?.id)
    ) {
      setSelectedAccount(null);
    }
  }, [exportTargets, selectedAccount]);

  useEffect(() => {
    if (spaces.length === 1) {
      setSelectedSpace(spaces[0]);
    }
    if (spaces.length === 0) {
      setSelectedSpace(null);
    }
  }, [spaces]);

  const classes = useExportChannelsStyles();

  return (
    <ModalMenuUI
      title="Export to Contentful • Select Account"
      loading={!hasResolved(exportTargetStatus)}
      onCloseEvent={previousModal}
      classes={{ contents: classes.modalContents }}
      wide={true}
      footerLeftComponent={
        <Button
          text={'Manage integration'}
          onClick={handleManageIntegration}
          colorVariant={'ghost1'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={previousModal}
            disabled={false}
          />
          <Button
            disabled={!selectedSpace}
            colorVariant={'filled1'}
            text={'Next'}
            onClick={handleNext}
          />
        </>
      }
    >
      {hasResolved(exportTargetStatus) && (
        <ExportChannelsPicker<
          ExportTarget<'contentful'>,
          ContentfulConfig['spaces'][number]
        >
          title="Contentful Space"
          noChannelsMessage="This Contentful account does not have access to any spaces."
          accounts={exportTargets}
          channels={spaces}
          selectedAccount={selectedAccount}
          selectedChannel={selectedSpace}
          onManageIntegration={handleManageIntegration}
          onSelectAccount={handleSelectAccount}
          onSelectChannel={setSelectedSpace}
        />
      )}
    </ModalMenuUI>
  );
};
